import { useState, useEffect, useRef, useCallback } from "react";
import { InfoTooltip } from "./InfoTooltip";
import { InlineChatBlock } from "./InlineChatBlock";
import { PLH_INLINE_CHAT_VISIBILITY, PLH_UI_VISIBILITY } from "./plhTileInfo";

/**
 * Interface representing a single signup record (table row).
 * Aligned with m_plh_new_signups backend response.
 */
/** Optional enrichment (e.g. from Firecrawl); not yet from backend. */
export interface NewSignupEnrichment {
    industry?: string;
    role?: string;
    employees?: string;
}

interface NewSignup {
    userId: string;
    teamId: string;
    userName: string;
    userEmail: string;
    companyName: string;
    status: "Activated" | "Schedule created" | "Invited teammates" | "Not activated";
    signedUpAt: string;
    source: "organic" | "paid" | "referral" | "direct";
    landing: string;
    referrer: string;
    utm: string;
    country: string;
    context: string;
    signUpReason: string;
    /**
     * Number of users on the Soon team at signup time.
     * Comes from m_plh_new_signups.teamUserCount.
     */
    teamUserCount: number;
    /** Activation progress from m_plh_new_signups. */
    assignedPeople: number;
    scheduledDays: number;
    scheduledHours: number;
    futureHours: number; // 0–100
    /** Journey event timestamps from m_plh_new_signups (may be null when not yet occurred). */
    firstInviteCreatedAt?: string | null;
    firstMemberCreatedAt?: string | null;
    firstAdminCreatedAt?: string | null;
    firstEventCreatedAt?: string | null;
    firstEventPublishedAt?: string | null;
    /** Enrichment data (industry, role, employees); backend may not send. */
    enrichment?: NewSignupEnrichment;
    /** Whether signup matches ICP; backend may not send. */
    icp?: boolean;
}

/** Alias for table row data. */
type NewSignupRow = NewSignup;

/**
 * Panel detail superset. Many fields are optional (not yet in m_plh_new_signups).
 */
interface SignupUserDetail {
    userName: string;
    userEmail: string;
    companyName: string;
    status: NewSignup["status"];
    signedUpAt: string;
    source: NewSignup["source"];
    landing: string;
    country: string;
    context: string;
    signUpReason: string;
    authMethod?: string;
    /**
     * UI-friendly alias for backend teamUserCount.
     */
    teamSize?: number;
    assignedPeople?: number;
    scheduledHours?: number;
    scheduledDays?: number;
    /** Display string e.g. "80%". */
    futureRatio?: string;
    /** Numeric 0–100 for threshold check. */
    futureHours?: number;
    icp?: boolean;
    /** Per-step journey timestamps (raw ISO strings). */
    scheduleGeneratedAt?: string;
    schedulePublishedAt?: string;
    teammatesInvitedAt?: string;
    /** Firecrawl-style enrichment for panel; optional. */
    firecrawl?: {
        company?: string;
        industry?: string;
        employees?: string;
        role?: string;
        city?: string;
        website?: string;
    };
}

interface NewSignupsTileProps {
    signups: NewSignup[];
    count: number;
}

/**
 * Maps status to mock badge class (sb-na, sb-inv, sb-sc, sb-act).
 */
const getStatusBadgeClass = (status: NewSignup["status"]): string => {
    switch (status) {
        case "Activated":
            return "sb-act";
        case "Schedule created":
            return "sb-sc";
        case "Invited teammates":
            return "sb-inv";
        case "Not activated":
        default:
            return "sb-na";
    }
};

/**
 * Formats signup date for display (e.g. "8 Feb 21:20").
 */
const formatSignupDateInline = (dateStr: string): string => {
    try {
        const date = new Date(dateStr);
        const day = date.getDate();
        const month = date.toLocaleString("en-US", { month: "short" });
        const timeStr = date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });
        return `${day} ${month} ${timeStr}`;
    } catch {
        return "—";
    }
};

/**
 * Formats date and time for journey timeline (e.g. "8 Feb, 21:20") so time is clearly visible.
 */
const formatTimelineTimestamp = (dateStr: string): string => {
    try {
        const date = new Date(dateStr);
        const day = date.getDate();
        const month = date.toLocaleString("en-US", { month: "short" });
        const timeStr = date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });
        return `${day} ${month}, ${timeStr}`;
    } catch {
        return "—";
    }
};

/**
 * Combines country and context for display (e.g. "Poland · Chrome").
 */
const formatContext = (country: string, context: string): string => {
    const parts = [country, context].filter(Boolean).join(" · ");
    return parts || "—";
};

/** Format enrichment for table: industry + role/employees when present. */
const formatEnrichment = (enrichment?: NewSignupEnrichment): string => {
    if (!enrichment) return "—";
    const parts = [enrichment.industry, enrichment.role, enrichment.employees].filter(Boolean);
    return parts.length ? parts.join(" · ") : "—";
};

/**
 * Status badge component matching mock (sb with dot).
 */
const StatusBadge = ({ status }: { status: NewSignup["status"] }) => (
    <span className={`sb ${getStatusBadgeClass(status)}`}>
        <span className="d" />
        {status}
    </span>
);

/**
 * Source badge matching mock src-b style (subtle background).
 */
const SourceBadge = ({ source }: { source: NewSignup["source"] }) => (
    <span className="src-b">{source}</span>
);

/**
 * Build SignupUserDetail from NewSignupRow (with placeholders for missing fields).
 */
const toSignupUserDetail = (row: NewSignupRow): SignupUserDetail => {
    // Derive teammates invited timestamp as the earliest of invite/member/admin timestamps.
    const inviteCandidates = [
        row.firstInviteCreatedAt,
        row.firstMemberCreatedAt,
        row.firstAdminCreatedAt,
    ].filter((v): v is string => !!v);

    let teammatesInvitedAt: string | undefined;
    if (inviteCandidates.length > 0) {
        teammatesInvitedAt = inviteCandidates.reduce((earliest, current) =>
            new Date(current) < new Date(earliest) ? current : earliest
        );
    }

    const scheduleGeneratedAt = row.firstEventCreatedAt ?? undefined;
    const schedulePublishedAt = row.firstEventPublishedAt ?? undefined;

    return {
        userName: row.userName,
        userEmail: row.userEmail,
        companyName: row.companyName,
        status: row.status,
        signedUpAt: row.signedUpAt,
        source: row.source,
        landing: row.landing,
        country: row.country,
        context: row.context,
        signUpReason: row.signUpReason,
        authMethod: undefined,
        teamSize: row.teamUserCount && row.teamUserCount > 0 ? row.teamUserCount : undefined,
        assignedPeople: row.assignedPeople,
        scheduledHours: row.scheduledHours,
        scheduledDays: row.scheduledDays,
        futureRatio: row.futureHours != null ? `${row.futureHours}%` : undefined,
        futureHours: row.futureHours,
        icp: row.icp,
        scheduleGeneratedAt,
        schedulePublishedAt,
        teammatesInvitedAt,
        // Firecrawl block: optional; could be populated from row.enrichment or future API
        firecrawl: undefined,
    };
};

/**
 * New Signups Tile Component.
 * Displays a table of recent signups with a right-side slide-over User Detail Panel.
 */
export const NewSignupsTile = ({ signups, count }: NewSignupsTileProps) => {
    const [selectedUser, setSelectedUser] = useState<NewSignupRow | null>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const lastClickedRowRef = useRef<HTMLTableRowElement | null>(null);

    const closePanel = useCallback(() => {
        setSelectedUser(null);
        document.body.style.overflow = "";
        lastClickedRowRef.current?.focus();
        lastClickedRowRef.current = null;
    }, []);

    const openPanel = useCallback((user: NewSignupRow, rowEl: HTMLTableRowElement | null) => {
        lastClickedRowRef.current = rowEl;
        setSelectedUser(user);
        document.body.style.overflow = "hidden";
        requestAnimationFrame(() => {
            closeButtonRef.current?.focus();
        });
    }, []);

    useEffect(() => {
        if (!selectedUser) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                closePanel();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [selectedUser, closePanel]);

    const isOpen = selectedUser !== null;

    return (
        <div className="signups-section">
            {/* Source indicator + InfoTooltip (preserved) */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 12,
                }}
            >
                <div
                    style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: "var(--green)",
                    }}
                />
                <span style={{ fontSize: 12, fontWeight: 500, color: "var(--green)" }}>
                    PostHog API
                </span>
                <InfoTooltip text="All signups recorded by PostHog for the selected full week, including attribution such as source, landing page, referrer, and UTM parameters." />
            </div>

            <div className="signups-card">
                <div className="signups-top">
                    <h3>New signups</h3>
                    <span className="s-cnt">{count}</span>
                </div>
                <table>
                    <thead>
                        <tr>
                            <th>User</th>
                            <th>Status</th>
                            <th>Enrichment</th>
                            <th>Signed up</th>
                            <th>Source</th>
                            <th>Landing</th>
                            <th>Context</th>
                            <th>ICP</th>
                        </tr>
                    </thead>
                    <tbody>
                        {signups.length === 0 ? (
                            <tr>
                                <td colSpan={8} style={{ textAlign: "center", padding: "32px 16px", color: "var(--t4)", fontSize: 14 }}>
                                    No signups found for this week
                                </td>
                            </tr>
                        ) : (
                            signups.map((signup) => (
                                <tr
                                    key={signup.userId}
                                    tabIndex={0}
                                    onClick={(e) => openPanel(signup, e.currentTarget)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            openPanel(signup, e.currentTarget);
                                        }
                                    }}
                                >
                                    <td>
                                        <div className="uc">
                                            <span className="un">{signup.userName}</span>
                                            <span className="ue">{signup.userEmail}</span>
                                            {signup.companyName && signup.companyName !== "—" && (
                                                <span className="uo">{signup.companyName}</span>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <StatusBadge status={signup.status} />
                                    </td>
                                    <td className="signups-dim">
                                        {signup.enrichment ? (
                                            <span>
                                                {formatEnrichment(signup.enrichment)}
                                                <span className="fc-badge" title="Enriched">FC</span>
                                            </span>
                                        ) : (
                                            "—"
                                        )}
                                    </td>
                                    <td className="signups-dim mono">
                                        {formatSignupDateInline(signup.signedUpAt)}
                                    </td>
                                    <td>
                                        <SourceBadge source={signup.source} />
                                    </td>
                                    <td className="signups-dim" title={signup.landing}>
                                        {signup.landing}
                                    </td>
                                    <td className="signups-dim">
                                        {formatContext(signup.country, signup.context)}
                                    </td>
                                    <td className="signups-dim">
                                        {signup.icp != null ? (signup.icp ? "Yes ✓" : "No") : "—"}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Overlay */}
            <div
                className={`plh-overlay ${isOpen ? "open" : ""}`}
                onClick={closePanel}
                aria-hidden={!isOpen}
            />

            {/* Slide-over Panel */}
            <div className={`plh-panel ${isOpen ? "open" : ""}`} aria-hidden={!isOpen}>
                {selectedUser && (
                    <>
                        <div className="plh-panel-hdr">
                            <h3>{selectedUser.userName}</h3>
                            <button
                                ref={closeButtonRef}
                                type="button"
                                className="plh-panel-close"
                                onClick={closePanel}
                                aria-label="Close panel"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="plh-panel-body">
                            <UserDetailPanelContent detail={toSignupUserDetail(selectedUser)} />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

/**
 * Panel body content: hero, p-grid, p-challenge (sign_up_reason), activation progress, timeline.
 */
const UserDetailPanelContent = ({ detail }: { detail: SignupUserDetail }) => {
    const initials = detail.userName
        .split(" ")
        .map((w) => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);

    const formattedDate = formatSignupDateInline(detail.signedUpAt);
    const formattedTimelineDate = formatTimelineTimestamp(detail.signedUpAt);
    const contextDisplay = formatContext(detail.country, detail.context);

    // Activation thresholds (aligned with backend ACTIVATION_PARAMETERS)
    const minPeople = 8;
    const minHours = 320;
    const minDays = 5;
    const minFuturePct = 80;
    const thresholds = [
        { label: "Assigned People", val: detail.assignedPeople ?? "—", threshold: "≥ 8", met: (detail.assignedPeople ?? 0) >= minPeople },
        { label: "Scheduled Hours", val: detail.scheduledHours ?? "—", threshold: "≥ 320", met: (detail.scheduledHours ?? 0) >= minHours },
        { label: "Scheduled Days", val: detail.scheduledDays ?? "—", threshold: "≥ 5", met: (detail.scheduledDays ?? 0) >= minDays },
        { label: "Future Hours", val: detail.futureRatio ?? "—", threshold: "≥ 80%", met: (detail.futureHours ?? 0) >= minFuturePct },
        { label: "ICP Match", val: detail.icp != null ? (detail.icp ? "Yes" : "No") : "—", threshold: "ON", met: false },
    ];
    const metCount = thresholds.filter((t) => t.met).length;
    const pctDone = Math.round((metCount / thresholds.length) * 100);

    // Journey timeline: each step derived from status/teamSize only (no fixed order after account setup)
    const accountSetupDone = detail.status !== "Not activated";
    // Backend "Invited teammates" means no schedule (se is NULL); only "Schedule created" or "Activated" imply a schedule exists.
    const hasSchedule =
        detail.status === "Schedule created" ||
        detail.status === "Activated";
    const hasTeammates =
        (detail.teamSize != null && detail.teamSize > 1) ||
        detail.status === "Invited teammates" ||
        detail.status === "Activated";
    const isActivated = detail.status === "Activated";

    // Per-step timestamps: prefer specific event timestamps when available, otherwise show "—" even if step is done.
    const signedUpDisplay = formattedTimelineDate;

    const accountSetupDate = accountSetupDone ? signedUpDisplay : "—";

    let scheduleGeneratedDate = "—";
    let scheduleGeneratedRaw: string | undefined;
    if (hasSchedule && detail.scheduleGeneratedAt) {
        scheduleGeneratedDate = formatTimelineTimestamp(detail.scheduleGeneratedAt);
        scheduleGeneratedRaw = detail.scheduleGeneratedAt;
    }

    let teammatesInvitedDate = "—";
    let teammatesInvitedRaw: string | undefined;
    if (hasTeammates && detail.teammatesInvitedAt) {
        teammatesInvitedDate = formatTimelineTimestamp(detail.teammatesInvitedAt);
        teammatesInvitedRaw = detail.teammatesInvitedAt;
    }

    let schedulePublishedDate = "—";
    let schedulePublishedRaw: string | undefined;
    if (isActivated && detail.schedulePublishedAt) {
        schedulePublishedDate = formatTimelineTimestamp(detail.schedulePublishedAt);
        schedulePublishedRaw = detail.schedulePublishedAt;
    }

    const steps = [
        {
            label: "Signed up",
            done: true,
            date: signedUpDisplay,
            rawTimestamp: detail.signedUpAt,
        },
        {
            label: "Account setup finished",
            done: accountSetupDone,
            date: accountSetupDone ? signedUpDisplay : "—",
            rawTimestamp: accountSetupDone ? detail.signedUpAt : undefined,
        },
        {
            label: "Schedule generated",
            done: hasSchedule,
            date: scheduleGeneratedDate,
            rawTimestamp: scheduleGeneratedRaw,
        },
        {
            label: "Teammates invited",
            done: hasTeammates,
            date: teammatesInvitedDate,
            rawTimestamp: teammatesInvitedRaw,
        },
        {
            label: "Schedule published",
            done: isActivated,
            date: schedulePublishedDate,
            rawTimestamp: schedulePublishedRaw,
        },
    ];

    const placeholder = "—";

    return (
        <>
            <div className="p-hero">
                <div className="p-avatar">{initials}</div>
                <div>
                    <div className="p-name">{detail.userName}</div>
                    <div className="p-email">{detail.userEmail}</div>
                    {detail.companyName && detail.companyName !== "—" && (
                        <div className="p-org">{detail.companyName}</div>
                    )}
                </div>
            </div>

            <div className="p-grid">
                <div className="p-stat">
                    <div className="ps-label">Status</div>
                    <div className="ps-val">{detail.status}</div>
                </div>
                <div className="p-stat">
                    <div className="ps-label">Auth Method</div>
                    <div className="ps-val">{detail.authMethod ?? placeholder}</div>
                    {/* TODO: authMethod not in m_plh_new_signups */}
                </div>
                <div className="p-stat">
                    <div className="ps-label">Team Size</div>
                    <div className="ps-val">{detail.teamSize != null ? detail.teamSize : placeholder}</div>
                </div>
                <div className="p-stat">
                    <div className="ps-label">Signed Up</div>
                    <div className="ps-val mono" style={{ fontSize: "0.78rem" }}>{formattedDate}</div>
                </div>
                <div className="p-stat">
                    <div className="ps-label">Source</div>
                    <div className="ps-val">{detail.source}</div>
                </div>
                <div className="p-stat">
                    <div className="ps-label">Landing Page</div>
                    <div className="ps-val" style={{ fontSize: "0.72rem" }}>{detail.landing}</div>
                </div>
                <div className="p-stat">
                    <div className="ps-label">Context</div>
                    <div className="ps-val" style={{ fontSize: "0.72rem" }}>{contextDisplay}</div>
                </div>
                <div className="p-stat">
                    <div className="ps-label">ICP Match</div>
                    <div className="ps-val">{detail.icp != null ? (detail.icp ? "Yes ✓" : "No ✗") : placeholder}</div>
                    {/* TODO: icp not in m_plh_new_signups */}
                </div>
            </div>

            {detail.signUpReason && detail.signUpReason !== "—" ? (
                <div className="p-challenge">
                    <div className="pc-label">Challenge / Use Case</div>
                    <div className="pc-text">{detail.signUpReason}</div>
                </div>
            ) : (
                <div className="p-challenge">
                    <div className="pc-label">Challenge / Use Case</div>
                    <div className="pc-text">Not available yet</div>
                    {/* TODO: sign_up_reason is wired; empty when user did not provide */}
                </div>
            )}

            <div className="p-section-title">Activation Progress · {pctDone}% ({metCount}/5 met)</div>
            <div
                style={{
                    height: 6,
                    background: "var(--s3)",
                    borderRadius: 3,
                    marginBottom: 14,
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        height: "100%",
                        width: `${pctDone}%`,
                        background: pctDone >= 80 ? "var(--green)" : pctDone >= 40 ? "var(--amber)" : "var(--red)",
                        borderRadius: 3,
                        transition: "width 0.5s",
                    }}
                />
            </div>
            <div className="p-act-grid">
                {thresholds.map((t) => (
                    <div key={t.label} className="p-act-item">
                        <div className="pa-l">{t.label}</div>
                        <div className={`pa-v ${t.met ? "pa-ok" : "pa-miss"}`}>{String(t.val)}</div>
                        <div className="pa-threshold">{t.threshold} {t.met ? "✓" : "✗"}</div>
                    </div>
                ))}
            </div>

            <div className="p-section-title">Journey Timeline</div>
            <div className="p-timeline">
                {steps.map((s) => (
                    <div key={s.label} className="p-tl-item">
                        <div className={`p-tl-dot ${s.done ? "done" : "pending"}`} />
                        <div className="p-tl-text">
                            {s.label}
                            <span className="date" title={s.rawTimestamp}>
                                {s.date}
                            </span>
                        </div>
                    </div>
                ))}
            </div>

            {PLH_UI_VISIBILITY["firecrawl-enrichment"] !== false && (
            <div className="fc-enrich">
                <div className="fc-hdr">Firecrawl Enrichment</div>
                <div className="fc-rows">
                    <div className="fc-row"><span className="fc-k">Company</span><span className="fc-v">{detail.firecrawl?.company ?? placeholder}</span></div>
                    <div className="fc-row"><span className="fc-k">Industry</span><span className="fc-v">{detail.firecrawl?.industry ?? placeholder}</span></div>
                    <div className="fc-row"><span className="fc-k">Employees</span><span className="fc-v">{detail.firecrawl?.employees ?? placeholder}</span></div>
                    <div className="fc-row"><span className="fc-k">Role</span><span className="fc-v">{detail.firecrawl?.role ?? placeholder}</span></div>
                    <div className="fc-row"><span className="fc-k">City</span><span className="fc-v">{detail.firecrawl?.city ?? placeholder}</span></div>
                    <div className="fc-row"><span className="fc-k">Website</span><span className="fc-v">{detail.firecrawl?.website ?? placeholder}</span></div>
                </div>
            </div>
            )}

            {PLH_INLINE_CHAT_VISIBILITY["user-detail"] !== false && (
                <InlineChatBlock
                    title="✦ Ask AI about this user"
                    initialMessage="I can help you understand this user's fit, suggest follow-up actions, or compare with ICP. What would you like to know?"
                    placeholder="e.g. How does this user compare to our ICP?"
                    contextLabel={detail.userName}
                />
            )}
        </>
    );
};
